import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { CONFIG_CONTEXT } from 'src/app/enum/config-context.enum';
import { DiscountTypeEnum } from 'src/app/enum/discount-type.enum';
import { LiteOfferResult } from 'src/app/models/configurators-2d/tunnel/kitchen/kitchen-tunnel-store-model';
import { BathroomHbmService } from 'src/app/services/configurators-2d/applications/bathroom-hbm.service';
import { BathroomHbmConfigurationData } from 'src/app/stores/tunnel/model/configuration-data/bathroom-hbm-configuration-data';
import { PricingState } from 'src/app/stores/tunnel/model/pricing-state';
import { FundingState } from 'src/app/stores/tunnel/model/funding-state';
import { Funding } from 'src/app/stores/tunnel/model/funding';
import { TunnelDataStore } from 'src/app/stores/tunnel/tunnel-data.store';
import { TunnelNavigationStore } from 'src/app/stores/tunnel/tunnel-navigation.store';
import { BaseTunnelObject } from '../../model/base-tunnel-object';
import { FeatureSettingsService } from 'src/app/services/feature-settings/feature-settings.service';
import { StatisticService } from 'src/app/services/statistic.service';
import { StatisticTypeEnum } from 'src/app/enum/statistic-type.enum';
import { logError } from 'src/app/services/helpers/sentry.helper';
import { isFundingEmpty, isFundingStateEmpty } from 'src/app/services/helpers/funding.helper';

type Column = {
  header: string;
  field: string;
};

/**
 * Étape « Financement » du tunnel SDB ByMe/HBM 3D (CONFIG_CONTEXT.BATHROOM).
 */
@Component({
  selector: 'ng-casto-bathroom-hbm-tunnel-funding',
  templateUrl: './bathroom-hbm-tunnel-funding.component.html',
})
export class BathroomHbmTunnelFundingComponent extends BaseTunnelObject implements OnInit, OnDestroy {
  // Services
  protected bathroomHbmService = inject(BathroomHbmService);
  protected featureSettingsService = inject(FeatureSettingsService);
  private readonly statisticService = inject(StatisticService);

  // Properties
  protected currentPricing: PricingState;
  protected currentFunding: FundingState;
  protected currentDiscount: DiscountTypeEnum = null;
  protected currentWithInstallation: boolean = null;
  protected loading: boolean = false;
  protected pricingLoading: boolean = false;
  protected downPayment: number = 0;
  private bathroomHbmConfiguration: BathroomHbmConfigurationData;

  protected readonly offersColumns: Column[] = [
    { header: 'Barème', field: 'code' },
    { header: 'Offre', field: 'offer' },
    { header: 'Mensualité', field: 'mensuality' },
    { header: 'Durée', field: 'duration' },
    { header: 'Total intérêts', field: 'interest' },
    { header: 'Total dû', field: 'total' },
    { header: 'TAEG', field: 'taeg' },
  ];

  constructor() {
    const tunnelNavigationStore = inject(TunnelNavigationStore);
    const tunnelDataStore = inject(TunnelDataStore);
    super(tunnelNavigationStore, tunnelDataStore);

    this.tunnelNavigationStore = tunnelNavigationStore;
    this.tunnelDataStore = tunnelDataStore;
  }

  /** Pose masquée si le feature setting est coupé ou si l'environnement la désactive. */
  get disableInstallation(): boolean {
    return !this.featureSettingsService.poseEnabled || this.bathroomHbmService.isInstallationDisabled();
  }

  get offers(): LiteOfferResult[] {
    if (!this.currentFunding) {
      return [];
    }
    return this.funding.offerTable;
  }

  get funding(): Funding {
    if (this.currentFunding) {
      return this.currentFunding[
        this.currentWithInstallation ? 'fundingWithInstallation' : 'fundingWithoutInstallation'
      ];
    }
  }

  get offersWithFooter() {
    return this.offers.length > 0 ? [...this.offers, { isTextContainer: true }] : [];
  }

  get fundingAmount(): number {
    return Math.max((this.currentPricing?.totalWithoutFunding?.actualAmount ?? 0) - (this.downPayment ?? 0), 0);
  }

  get displayDiscount(): boolean {
    return this.discountNumberValue > 0;
  }

  get discountNumberValue(): number {
    switch (this.currentDiscount) {
      case DiscountTypeEnum.Discount0:
        return 0;
      case DiscountTypeEnum.Discount5:
        return 5;
      case DiscountTypeEnum.Discount10:
        return 10;
      case DiscountTypeEnum.Discount20:
        return 20;
    }
  }

  ngOnInit(): void {
    super.ngOnInit();
    void this.initFundingStep();
  }

  private async initFundingStep(): Promise<void> {
    // Restaure l'apport persisté avant un éventuel recalcul.
    this.downPayment = this.currentFunding?.downPayment ?? 0;
    await this.initFundingIfNeeded();
    this.downPayment = this.currentFunding?.downPayment ?? this.downPayment;
    this.sendAccessFundingStatistic();
  }

  /** Envoi de la stat d'accès à la page de simulation de financement. */
  private sendAccessFundingStatistic(): void {
    this.statisticService.sendStatistic(
      StatisticTypeEnum.FUNDING_COUNT,
      1,
      CONFIG_CONTEXT.BATHROOM,
      this.bathroomHbmConfiguration?.configurationId
    );
  }

  protected isNoOffer(): boolean {
    return isFundingEmpty(this.funding);
  }

  protected subscribeToObservable() {
    this.subscriptionList.push(
      this.tunnelDataStore.getCurrentPricing().subscribe(currentPricing => {
        if (currentPricing) {
          this.currentPricing = currentPricing;
        }
      }),
      this.tunnelDataStore.getCurrentFunding().subscribe(currentFunding => {
        if (currentFunding) {
          this.currentFunding = currentFunding;
        }
      }),
      this.tunnelDataStore.getCurrentWithInstallation().subscribe(async currentWithInstallation => {
        if (currentWithInstallation !== undefined && this.currentWithInstallation !== currentWithInstallation) {
          let lastCurrentValue = this.currentWithInstallation;
          this.currentWithInstallation = currentWithInstallation;
          if (lastCurrentValue !== null) {
            await this.updateFunding();
          }
        }
      }),
      this.tunnelDataStore.getCurrentDiscount().subscribe(async currentDiscount => {
        if (currentDiscount && this.currentDiscount !== currentDiscount) {
          this.currentDiscount = currentDiscount;
        }
      }),
      this.tunnelDataStore.getConfigurationData().subscribe(configurationData => {
        if (configurationData) {
          this.bathroomHbmConfiguration = configurationData.get(CONFIG_CONTEXT.BATHROOM) as BathroomHbmConfigurationData;
        }
      }),
      this.tunnelDataStore.getPricingLoading().subscribe(loading => {
        this.pricingLoading = loading;
      })
    );
  }

  private async computeFunding() {
    await this.bathroomHbmService.computeFunding({
      tunnelConfigurationId: this.bathroomHbmConfiguration.configurationId,
      downPaymentAmount: this.downPayment,
      shouldRecalculateFinancing: true,
    });
  }

  protected async updateFunding() {
    try {
      this.loading = true;
      // Pas de court-circuit quand le financement est coupé : le service retombe sur un
      // rechargement du pricing, sinon les totaux resteraient sur l'ancien scénario de pose.
      await this.computeFunding();
      this.checkDownPayment();
    } catch (error) {
      logError(error);
    } finally {
      this.loading = false;
    }
  }

  private async initFundingIfNeeded() {
    const financementEnabled = await this.featureSettingsService.isFinancementEnabled();

    if (!financementEnabled) {
      await this.ensurePricingIfNeeded();
      this.tunnelDataStore.disableCurrentFunding();
      return;
    }

    // Valeur autoritaire du store : `this.currentFunding` n'est pas remis à `undefined` par
    // `resetCurrentFunding()`, la souscription filtrant sur `if (funding)`.
    if (isFundingStateEmpty(this.tunnelDataStore.getCurrentStateValue().currentFunding)) {
      await this.updateFunding();
    }
  }

  private async ensurePricingIfNeeded(): Promise<void> {
    if (!this.currentPricing?.totalWithoutFunding) {
      await this.bathroomHbmService.loadBathroomHbmPricing(this.bathroomHbmConfiguration.configurationId);
    }
  }

  private checkDownPayment() {
    const totalWithoutFunding = this.currentPricing?.totalWithoutFunding?.actualAmount ?? 0;
    if (this.downPayment > totalWithoutFunding) {
      this.downPayment = totalWithoutFunding;
    }
  }

  protected async setWithInstallation(withInstallation: boolean) {
    try {
      this.loading = true;
      await this.bathroomHbmService.updateWithInstallation(withInstallation);
    } catch (error) {
      this.loading = false;
    } finally {
      this.loading = false;
    }
    this.tunnelDataStore.setWithInstallation(withInstallation);
  }

  protected isOfferSelected(offer: LiteOfferResult): boolean {
    if (offer) {
      return (
        this.funding.mensuality === offer.mensuality &&
        this.funding.duration === offer.duration &&
        this.funding.code === offer.code
      );
    }
  }

  protected async selectOffer(offer: LiteOfferResult) {
    try {
      this.loading = true;
      await this.bathroomHbmService.selectOffer(offer.code, offer.duration);
    } catch (error) {
      this.loading = false;
    } finally {
      this.loading = false;
    }
  }
}
